import { useState, useEffect } from 'react';
import { Save, Settings, Users, Mail, Shield, Sliders, Palette, Plus, Edit2, Trash2, Eye } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Alert from '../ui/Alert';
import TemplateForm from './TemplateForm';
import MaintenanceToggle from './MaintenanceToggle';
import * as emailTemplateService from '../../services/emailTemplateService';

export default function AdminSettings() {
  const [activeSection, setActiveSection] = useState('system');
  const [saveStatus, setSaveStatus] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [sendEmailConfig, setSendEmailConfig] = useState({
    recipient: 'all', // 'all', 'admins', 'specific'
    specificEmail: '',
    templateId: '',
    customSubject: '',
    customHeaderText: 'Message from CertifiAble',
    customMessage: ''
  });
  const [automationRules, setAutomationRules] = useState([
    {
      id: 'welcome',
      name: 'Welcome Email',
      trigger: 'user_signup',
      templateId: 'welcome-email',
      enabled: true,
      delay: 0
    },
    {
      id: 'cycle-reminder',
      name: 'Cycle End Reminder',
      trigger: 'cycle_end_30_days',
      templateId: '',
      enabled: false,
      delay: 0
    }
  ]);

  // System Configuration State
  const [systemConfig, setSystemConfig] = useState({
    ridCycleLength: 4,
    ridTotalCEUs: 8.0,
    ridProfessionalStudies: 6.0,
    ridGeneralStudies: 2.0,
    ridPPO: 0.0,
    beiCycleLength: 5,
    beiTotalCEUs: 10.0,
    beiProfessionalStudies: 6.0,
    beiGeneralStudies: 2.0,
    beiPPO: 2.0
  });

  // User Management State
  const [userConfig, setUserConfig] = useState({
    autoApprove: true,
    requireEmailVerification: true,
    defaultPermissions: 'user',
    allowSelfRegistration: true
  });

  // Email & Notifications State
  const [emailConfig, setEmailConfig] = useState({
    enableReminders: true,
    reminderFrequency: 'monthly',
    extensionNotifications: true,
    systemAnnouncements: true,
    announcementText: ''
  });

  // Data & Compliance State
  const [complianceConfig, setComplianceConfig] = useState({
    dataRetentionYears: 7,
    backupFrequency: 'daily',
    auditLogRetentionDays: 90,
    gdprCompliance: true,
    hipaaMode: false
  });

  // Application Settings State
  const [appConfig, setAppConfig] = useState({
    maintenanceMode: false,
    featureFlags: {
      googleSignIn: true,
      csvExport: true,
      profilePhotos: true,
      extensions: true
    },
    apiRateLimit: 1000,
    maxFileUploadMB: 10
  });

  // Branding State
  const [brandingConfig, setBrandingConfig] = useState({
    orgName: 'CertifiAble',
    primaryColor: '#3B82F6',
    secondaryColor: '#8B5CF6',
    logoUrl: '',
    customTermsUrl: ''
  });

  // Load email templates on mount
  useEffect(() => {
    if (activeSection === 'email') {
      loadTemplates();
    }
  }, [activeSection]);

  const loadTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const templates = await emailTemplateService.getAllTemplates();
      setEmailTemplates(templates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setSaveStatus({ type: 'error', message: 'Failed to load email templates' });
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await emailTemplateService.deleteTemplate(templateId);
      setSaveStatus({ type: 'success', message: 'Template deleted successfully' });
      loadTemplates();
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Failed to delete template' });
    }
  };

  const handleOpenSendEmail = async (recipient) => {
    setSendEmailConfig({
      recipient,
      specificEmail: '',
      templateId: emailTemplates[0]?.templateId || '',
      customSubject: '',
      customHeaderText: 'Message from CertifiAble',
      customMessage: ''
    });
    
    // Load all users for autocomplete
    // TODO: Replace with actual user service call
    setAllUsers([
      { email: 'dan@certifiable.me', name: 'Dan Frank' },
      { email: 'user@example.com', name: 'Test User' }
    ]);
    
    setShowSendEmailModal(true);
  };

  const handleSendEmail = async () => {
    // Confirmation
    const recipientCount = sendEmailConfig.recipient === 'all' ? 'all users' : 
                          sendEmailConfig.recipient === 'admins' ? 'all admins' : 
                          sendEmailConfig.specificEmail;
    
    if (!window.confirm(`Send email to ${recipientCount}?`)) return;
    
    // TODO: Implement actual email sending via AWS SES
    console.log('Sending email:', sendEmailConfig);
    alert('Email sending will be implemented with AWS SES integration');
    setShowSendEmailModal(false);
  };

  const handleSaveRule = () => {
    if (editingRule.id) {
      // Update existing rule
      setAutomationRules(automationRules.map(r => r.id === editingRule.id ? editingRule : r));
    } else {
      // Add new rule
      setAutomationRules([...automationRules, { ...editingRule, id: `rule_${Date.now()}` }]);
    }
    setEditingRule(null);
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      if (editingTemplate) {
        await emailTemplateService.updateTemplate(editingTemplate.templateId, templateData);
        setSaveStatus({ type: 'success', message: 'Template updated successfully' });
      } else {
        await emailTemplateService.createTemplate(templateData);
        setSaveStatus({ type: 'success', message: 'Template created successfully' });
      }
      setShowTemplateForm(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Failed to save template' });
    }
  };

  const handleSave = async (section) => {
    setIsSaving(true);
    setSaveStatus(null);

    try {
      // Simulate API call - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // TODO: Implement actual save to DynamoDB or API
      console.log(`Saving ${section} configuration:`, {
        systemConfig,
        userConfig,
        emailConfig,
        complianceConfig,
        appConfig,
        brandingConfig
      });

      setSaveStatus({ type: 'success', message: 'Settings saved successfully!' });
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Failed to save settings. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const sections = [
    { id: 'system', label: 'System Configuration', icon: Settings },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'email', label: 'Email & Notifications', icon: Mail },
    { id: 'compliance', label: 'Data & Compliance', icon: Shield },
    { id: 'application', label: 'Application Settings', icon: Sliders },
    { id: 'branding', label: 'Branding', icon: Palette }
  ];

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1 border-r border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Settings Sections
          </h3>
          <nav className="space-y-1">
            {sections.map(section => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3 p-6">
          {saveStatus && (
            <Alert type={saveStatus.type} className="mb-6">
              {saveStatus.message}
            </Alert>
          )}

          {/* System Configuration */}
          {activeSection === 'system' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">System Configuration</h2>
              
              {/* RID Settings */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">RID Certification Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Cycle Length (years)"
                    type="number"
                    value={systemConfig.ridCycleLength}
                    onChange={(e) => setSystemConfig({...systemConfig, ridCycleLength: parseInt(e.target.value)})}
                  />
                  <Input
                    label="Total CEUs Required"
                    type="number"
                    step="0.1"
                    value={systemConfig.ridTotalCEUs}
                    onChange={(e) => setSystemConfig({...systemConfig, ridTotalCEUs: parseFloat(e.target.value)})}
                  />
                  <Input
                    label="Professional Studies (minimum)"
                    type="number"
                    step="0.1"
                    value={systemConfig.ridProfessionalStudies}
                    onChange={(e) => setSystemConfig({...systemConfig, ridProfessionalStudies: parseFloat(e.target.value)})}
                  />
                  <Input
                    label="General Studies (minimum)"
                    type="number"
                    step="0.1"
                    value={systemConfig.ridGeneralStudies}
                    onChange={(e) => setSystemConfig({...systemConfig, ridGeneralStudies: parseFloat(e.target.value)})}
                  />
                  <Input
                    label="PPO (minimum)"
                    type="number"
                    step="0.1"
                    value={systemConfig.ridPPO}
                    onChange={(e) => setSystemConfig({...systemConfig, ridPPO: parseFloat(e.target.value)})}
                  />
                </div>
              </div>

              {/* BEI Settings */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">BEI Certification Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Cycle Length (years)"
                    type="number"
                    value={systemConfig.beiCycleLength}
                    onChange={(e) => setSystemConfig({...systemConfig, beiCycleLength: parseInt(e.target.value)})}
                  />
                  <Input
                    label="Total CEUs Required"
                    type="number"
                    step="0.1"
                    value={systemConfig.beiTotalCEUs}
                    onChange={(e) => setSystemConfig({...systemConfig, beiTotalCEUs: parseFloat(e.target.value)})}
                  />
                  <Input
                    label="Professional Studies (minimum)"
                    type="number"
                    step="0.1"
                    value={systemConfig.beiProfessionalStudies}
                    onChange={(e) => setSystemConfig({...systemConfig, beiProfessionalStudies: parseFloat(e.target.value)})}
                  />
                  <Input
                    label="General Studies (minimum)"
                    type="number"
                    step="0.1"
                    value={systemConfig.beiGeneralStudies}
                    onChange={(e) => setSystemConfig({...systemConfig, beiGeneralStudies: parseFloat(e.target.value)})}
                  />
                  <Input
                    label="PPO (minimum)"
                    type="number"
                    step="0.1"
                    value={systemConfig.beiPPO}
                    onChange={(e) => setSystemConfig({...systemConfig, beiPPO: parseFloat(e.target.value)})}
                  />
                </div>
              </div>

              <Button onClick={() => handleSave('system')} disabled={isSaving}>
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save System Configuration'}
              </Button>
            </div>
          )}

          {/* User Management */}
          {activeSection === 'users' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">User Management Settings</h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="auto-approve-checkbox" className="text-sm font-medium text-gray-900">Auto-approve new users</label>
                    <p id="auto-approve-description" className="text-sm text-gray-600">Automatically approve user registrations without manual review</p>
                  </div>
                  <input
                    id="auto-approve-checkbox"
                    type="checkbox"
                    checked={userConfig.autoApprove}
                    onChange={(e) => setUserConfig({...userConfig, autoApprove: e.target.checked})}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    aria-describedby="auto-approve-description"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="email-verification-checkbox" className="text-sm font-medium text-gray-900">Require email verification</label>
                    <p id="email-verification-description" className="text-sm text-gray-600">Users must verify their email before accessing the platform</p>
                  </div>
                  <input
                    id="email-verification-checkbox"
                    type="checkbox"
                    checked={userConfig.requireEmailVerification}
                    onChange={(e) => setUserConfig({...userConfig, requireEmailVerification: e.target.checked})}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    aria-describedby="email-verification-description"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="self-registration-checkbox" className="text-sm font-medium text-gray-900">Allow self-registration</label>
                    <p id="self-registration-description" className="text-sm text-gray-600">Users can create accounts without admin invitation</p>
                  </div>
                  <input
                    id="self-registration-checkbox"
                    type="checkbox"
                    checked={userConfig.allowSelfRegistration}
                    onChange={(e) => setUserConfig({...userConfig, allowSelfRegistration: e.target.checked})}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    aria-describedby="self-registration-description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Default permissions for new users</label>
                  <select
                    value={userConfig.defaultPermissions}
                    onChange={(e) => setUserConfig({...userConfig, defaultPermissions: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="user">User (Standard Access)</option>
                    <option value="admin">Admin (Full Access)</option>
                  </select>
                </div>
              </div>

              <Button onClick={() => handleSave('users')} disabled={isSaving} className="mt-6">
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save User Management Settings'}
              </Button>
            </div>
          )}

          {/* Email & Notifications */}
          {activeSection === 'email' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email & Notifications</h2>
              <p className="text-gray-600 mb-6">Manage email templates and automated notification rules</p>

              {/* Template Form */}
              {showTemplateForm && (
                <div className="bg-white border border-gray-200 rounded-lg mb-6">
                  <TemplateForm
                    template={editingTemplate}
                    onSave={handleSaveTemplate}
                    onCancel={() => { setShowTemplateForm(false); setEditingTemplate(null); }}
                  />
                </div>
              )}

              {/* Email Templates Section */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Email Templates</h3>
                    <p className="text-sm text-gray-600">Reusable email designs for automated and manual sending</p>
                  </div>
                  <Button onClick={() => { setShowTemplateForm(true); setEditingTemplate(null); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Template
                  </Button>
                </div>

                {isLoadingTemplates ? (
                  <p className="text-gray-600">Loading templates...</p>
                ) : emailTemplates.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                    <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-2">No email templates yet</p>
                    <p className="text-sm text-gray-500">Create your first template to get started</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {emailTemplates.map(template => (
                      <div key={template.templateId} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900">{template.templateName}</h4>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                template.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {template.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{template.subject}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{template.category}</span>
                              <span>Sent {template.sentCount || 0} times</span>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setPreviewTemplate(template)}
                              className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                              title="Preview"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setEditingTemplate(template); setShowTemplateForm(true); }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template.templateId)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Automation Rules Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Automation Rules</h3>
                    <p className="text-sm text-gray-600">Set up when to automatically send email templates</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setEditingRule({ name: '', trigger: '', templateId: '', enabled: true, delay: 0 })}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Rule
                  </Button>
                </div>

                <div className="space-y-3">
                  {automationRules.map(rule => (
                    <div key={rule.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{rule.name}</p>
                        <p className="text-sm text-gray-600">
                          Trigger: <span className="font-mono text-xs bg-white px-2 py-0.5 rounded border border-gray-200">{rule.trigger}</span>
                          {rule.templateId && ` → Template: ${rule.templateId}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={rule.enabled}
                            onChange={(e) => {
                              const updated = automationRules.map(r => 
                                r.id === rule.id ? {...r, enabled: e.target.checked} : r
                              );
                              setAutomationRules(updated);
                            }}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                          <span className={`px-2 py-1 text-xs rounded ${
                            rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {rule.enabled ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                        <button className="p-1 text-blue-600 hover:bg-blue-50 rounded" onClick={() => setEditingRule(rule)}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Manual Send Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Send Email Now</h3>
                <p className="text-sm text-gray-600 mb-4">Send a one-time email using an existing template or compose a new message</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={() => handleOpenSendEmail('all')}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email All Users
                  </Button>
                  <Button variant="outline" onClick={() => handleOpenSendEmail('admins')}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email Admins
                  </Button>
                  <Button variant="outline" onClick={() => handleOpenSendEmail('specific')}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email Specific User
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Data & Compliance */}
          {activeSection === 'compliance' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Data & Compliance</h2>
              
              <div className="space-y-6">
                <Input
                  label="Data retention period (years)"
                  type="number"
                  value={complianceConfig.dataRetentionYears}
                  onChange={(e) => setComplianceConfig({...complianceConfig, dataRetentionYears: parseInt(e.target.value)})}
                  helpText="How long to retain user data after account deletion"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Backup frequency</label>
                  <select
                    value={complianceConfig.backupFrequency}
                    onChange={(e) => setComplianceConfig({...complianceConfig, backupFrequency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>

                <Input
                  label="Audit log retention (days)"
                  type="number"
                  value={complianceConfig.auditLogRetentionDays}
                  onChange={(e) => setComplianceConfig({...complianceConfig, auditLogRetentionDays: parseInt(e.target.value)})}
                  helpText="How long to keep audit logs for compliance"
                />

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">GDPR compliance mode</label>
                    <p className="text-sm text-gray-600">Enable GDPR-compliant data handling and user rights</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={complianceConfig.gdprCompliance}
                    onChange={(e) => setComplianceConfig({...complianceConfig, gdprCompliance: e.target.checked})}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">HIPAA mode</label>
                    <p className="text-sm text-gray-600">Enable additional security measures for healthcare compliance</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={complianceConfig.hipaaMode}
                    onChange={(e) => setComplianceConfig({...complianceConfig, hipaaMode: e.target.checked})}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                </div>
              </div>

              <Button onClick={() => handleSave('compliance')} disabled={isSaving} className="mt-6">
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Compliance Settings'}
              </Button>
            </div>
          )}

          {/* Application Settings */}
          {activeSection === 'application' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Application Settings</h2>
              
              {/* Maintenance Mode Section */}
              <MaintenanceToggle />
              
              <div className="space-y-6 mt-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Feature Flags</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label htmlFor="google-signin-checkbox" className="text-sm font-medium text-gray-700">Google Sign-In</label>
                      <div className="relative">
                        <input
                          id="google-signin-checkbox"
                          type="checkbox"
                          checked={appConfig.featureFlags.googleSignIn}
                          onChange={(e) => setAppConfig({
                            ...appConfig,
                            featureFlags: {...appConfig.featureFlags, googleSignIn: e.target.checked}
                          })}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <div className="absolute inset-0 -m-3 min-h-[44px] min-w-[44px]" aria-hidden="true"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label htmlFor="csv-export-checkbox" className="text-sm font-medium text-gray-700">CSV Export</label>
                      <div className="relative">
                        <input
                          id="csv-export-checkbox"
                          type="checkbox"
                          checked={appConfig.featureFlags.csvExport}
                          onChange={(e) => setAppConfig({
                            ...appConfig,
                            featureFlags: {...appConfig.featureFlags, csvExport: e.target.checked}
                          })}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <div className="absolute inset-0 -m-3 min-h-[44px] min-w-[44px]" aria-hidden="true"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label htmlFor="profile-photos-checkbox" className="text-sm font-medium text-gray-700">Profile Photos</label>
                      <div className="relative">
                        <input
                          id="profile-photos-checkbox"
                          type="checkbox"
                          checked={appConfig.featureFlags.profilePhotos}
                          onChange={(e) => setAppConfig({
                            ...appConfig,
                            featureFlags: {...appConfig.featureFlags, profilePhotos: e.target.checked}
                          })}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <div className="absolute inset-0 -m-3 min-h-[44px] min-w-[44px]" aria-hidden="true"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <label htmlFor="extensions-checkbox" className="text-sm font-medium text-gray-700">Extensions</label>
                      <div className="relative">
                        <input
                          id="extensions-checkbox"
                          type="checkbox"
                          checked={appConfig.featureFlags.extensions}
                          onChange={(e) => setAppConfig({
                            ...appConfig,
                            featureFlags: {...appConfig.featureFlags, extensions: e.target.checked}
                          })}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <div className="absolute inset-0 -m-3 min-h-[44px] min-w-[44px]" aria-hidden="true"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <Input
                  label="API rate limit (requests/second)"
                  type="number"
                  value={appConfig.apiRateLimit}
                  onChange={(e) => setAppConfig({...appConfig, apiRateLimit: parseInt(e.target.value)})}
                  helpText="Maximum API requests per second per user"
                />

                <Input
                  label="Max file upload size (MB)"
                  type="number"
                  value={appConfig.maxFileUploadMB}
                  onChange={(e) => setAppConfig({...appConfig, maxFileUploadMB: parseInt(e.target.value)})}
                  helpText="Maximum file size for certificate uploads"
                />
              </div>

              <Button onClick={() => handleSave('application')} disabled={isSaving} className="mt-6">
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Application Settings'}
              </Button>
            </div>
          )}

          {/* Branding */}
          {activeSection === 'branding' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Branding</h2>
              
              <div className="space-y-6">
                <Input
                  label="Organization name"
                  type="text"
                  value={brandingConfig.orgName}
                  onChange={(e) => setBrandingConfig({...brandingConfig, orgName: e.target.value})}
                  helpText="Display name for your organization"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="primary-color-picker" className="block text-sm font-medium text-gray-900 mb-2">Primary color</label>
                    <div className="flex gap-2">
                      <input
                        id="primary-color-picker"
                        type="color"
                        value={brandingConfig.primaryColor}
                        onChange={(e) => setBrandingConfig({...brandingConfig, primaryColor: e.target.value})}
                        className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                        aria-label="Primary color picker"
                      />
                      <Input
                        type="text"
                        value={brandingConfig.primaryColor}
                        onChange={(e) => setBrandingConfig({...brandingConfig, primaryColor: e.target.value})}
                        placeholder="#3B82F6"
                        aria-label="Primary color hex value"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="secondary-color-picker" className="block text-sm font-medium text-gray-900 mb-2">Secondary color</label>
                    <div className="flex gap-2">
                      <input
                        id="secondary-color-picker"
                        type="color"
                        value={brandingConfig.secondaryColor}
                        onChange={(e) => setBrandingConfig({...brandingConfig, secondaryColor: e.target.value})}
                        className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                        aria-label="Secondary color picker"
                      />
                      <Input
                        type="text"
                        value={brandingConfig.secondaryColor}
                        onChange={(e) => setBrandingConfig({...brandingConfig, secondaryColor: e.target.value})}
                        placeholder="#8B5CF6"
                        aria-label="Secondary color hex value"
                      />
                    </div>
                  </div>
                </div>

                <Input
                  label="Logo URL"
                  type="url"
                  value={brandingConfig.logoUrl}
                  onChange={(e) => setBrandingConfig({...brandingConfig, logoUrl: e.target.value})}
                  helpText="URL to your organization's logo"
                  placeholder="https://example.com/logo.png"
                />

                <Input
                  label="Custom Terms of Service URL"
                  type="url"
                  value={brandingConfig.customTermsUrl}
                  onChange={(e) => setBrandingConfig({...brandingConfig, customTermsUrl: e.target.value})}
                  helpText="Link to your custom terms of service (optional)"
                  placeholder="https://example.com/terms"
                />

                <Alert type="info">
                  <strong>Note:</strong> Branding changes will apply to all users. Custom branding is designed for multi-tenant or white-label deployments.
                </Alert>
              </div>

              <Button onClick={() => handleSave('branding')} disabled={isSaving} className="mt-6">
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Branding Settings'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Automation Rule Modal */}
      {editingRule && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="automation-rule-modal-title"
          onKeyDown={(e) => e.key === 'Escape' && setEditingRule(null)}
        >
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="border-b border-gray-200 p-4 flex justify-between items-center">
              <h3 id="automation-rule-modal-title" className="text-lg font-semibold text-gray-900">
                {editingRule.id ? 'Edit Automation Rule' : 'Add Automation Rule'}
              </h3>
              <button 
                onClick={() => setEditingRule(null)} 
                className="text-gray-500 hover:text-gray-700 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Close dialog"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Input
                label="Rule Name"
                value={editingRule.name}
                onChange={(e) => setEditingRule({...editingRule, name: e.target.value})}
                placeholder="Welcome Email"
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Event</label>
                <select
                  value={editingRule.trigger}
                  onChange={(e) => setEditingRule({...editingRule, trigger: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select trigger...</option>
                  <option value="user_signup">User signs up</option>
                  <option value="cycle_end_30_days">30 days before cycle ends</option>
                  <option value="cycle_end_7_days">7 days before cycle ends</option>
                  <option value="cycle_ended">Cycle has ended</option>
                  <option value="ceu_uploaded">CEU uploaded</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Template</label>
                <select
                  value={editingRule.templateId}
                  onChange={(e) => setEditingRule({...editingRule, templateId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select template...</option>
                  {emailTemplates.map(template => (
                    <option key={template.templateId} value={template.templateId}>
                      {template.templateName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingRule.enabled}
                  onChange={(e) => setEditingRule({...editingRule, enabled: e.target.checked})}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label className="text-sm text-gray-700">Enable this rule</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button variant="outline" onClick={() => setEditingRule(null)}>Cancel</Button>
                <Button onClick={handleSaveRule}>Save Rule</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showSendEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Send Email</h3>
              <button
                onClick={() => setShowSendEmailModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Recipient */}
              <div>
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700 mb-2">Recipients</legend>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="email-recipients"
                        checked={sendEmailConfig.recipient === 'all'}
                        onChange={() => setSendEmailConfig({...sendEmailConfig, recipient: 'all'})}
                        className="h-4 w-4 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">All Users</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="email-recipients"
                        checked={sendEmailConfig.recipient === 'admins'}
                        onChange={() => setSendEmailConfig({...sendEmailConfig, recipient: 'admins'})}
                        className="h-4 w-4 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">Admins Only</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="email-recipients"
                        checked={sendEmailConfig.recipient === 'specific'}
                        onChange={() => setSendEmailConfig({...sendEmailConfig, recipient: 'specific'})}
                        className="h-4 w-4 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">Specific User</span>
                    </label>
                    {sendEmailConfig.recipient === 'specific' && (
                      <div className="ml-6 relative">
                        <Input
                          type="email"
                          value={sendEmailConfig.specificEmail}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSendEmailConfig({...sendEmailConfig, specificEmail: value});
                            // Filter users
                            if (value) {
                              const filtered = allUsers.filter(u => 
                                u.email.toLowerCase().includes(value.toLowerCase()) ||
                                u.name.toLowerCase().includes(value.toLowerCase())
                              );
                              setFilteredUsers(filtered);
                            } else {
                              setFilteredUsers([]);
                            }
                          }}
                          placeholder="Start typing name or email..."
                          aria-label="Specific user email address"
                        />
                        {/* Autocomplete dropdown */}
                        {filteredUsers.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {filteredUsers.map(user => (
                              <button
                                key={user.email}
                                type="button"
                                onClick={() => {
                                  setSendEmailConfig({...sendEmailConfig, specificEmail: user.email});
                                  setFilteredUsers([]);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 flex flex-col focus:outline-none focus:bg-gray-100"
                                aria-label={`Select ${user.name} (${user.email})`}
                              >
                                <span className="font-medium text-gray-900">{user.name}</span>
                                <span className="text-sm text-gray-600">{user.email}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </fieldset>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Template</label>
                <select
                  value={sendEmailConfig.templateId}
                  onChange={(e) => setSendEmailConfig({...sendEmailConfig, templateId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Custom Message (No Template)</option>
                  {emailTemplates.map(template => (
                    <option key={template.templateId} value={template.templateId}>
                      {template.templateName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Custom Subject (if no template or override) */}
              {(!sendEmailConfig.templateId || sendEmailConfig.customSubject) && (
                <Input
                  label="Subject Line"
                  value={sendEmailConfig.customSubject}
                  onChange={(e) => setSendEmailConfig({...sendEmailConfig, customSubject: e.target.value})}
                  placeholder="Enter email subject"
                />
              )}

              {/* Custom Message (if no template) */}
              {!sendEmailConfig.templateId && (
                <>
                  <Input
                    label="Email Header Text"
                    value={sendEmailConfig.customHeaderText}
                    onChange={(e) => setSendEmailConfig({...sendEmailConfig, customHeaderText: e.target.value})}
                    placeholder="Message from CertifiAble"
                    helpText="This appears in the blue gradient header"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea
                      value={sendEmailConfig.customMessage}
                      onChange={(e) => setSendEmailConfig({...sendEmailConfig, customMessage: e.target.value})}
                      rows={8}
                      placeholder="Enter your message..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </>
              )}

              {/* Preview selected template */}
              {sendEmailConfig.templateId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                  <div className="border border-gray-300 rounded-md p-4 bg-gray-50 max-h-60 overflow-y-auto">
                    {(() => {
                      const template = emailTemplates.find(t => t.templateId === sendEmailConfig.templateId);
                      return template ? (
                        <div>
                          <p className="text-sm font-semibold mb-2">{template.subject}</p>
                          <div dangerouslySetInnerHTML={{ __html: template.bodyHtml }} />
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowSendEmailModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="outline" onClick={() => {
                    alert('Test email will be sent to your admin email');
                  }}>
                    Send Test
                  </Button>
                  <Button onClick={handleSendEmail}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                </div>
              </div>

              {/* Preview Section */}
              {showPreview && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Email Preview</h4>
                  <div className="border border-gray-300 rounded-md p-4 bg-gray-50 max-h-96 overflow-y-auto">
                    {sendEmailConfig.templateId ? (
                      (() => {
                        const template = emailTemplates.find(t => t.templateId === sendEmailConfig.templateId);
                        return template ? (
                          <div>
                            <p className="text-sm font-semibold mb-3 pb-2 border-b border-gray-300">
                              Subject: {sendEmailConfig.customSubject || template.subject}
                            </p>
                            <div dangerouslySetInnerHTML={{ __html: template.bodyHtml }} />
                          </div>
                        ) : null;
                      })()
                    ) : (
                      <div>
                        <p className="text-sm font-semibold mb-3 pb-2 border-b border-gray-300">
                          Subject: {sendEmailConfig.customSubject || 'No subject'}
                        </p>
                        <div dangerouslySetInnerHTML={{ __html: `
                          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                            <div style="background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%); padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0;">
                              <h1 style="color: white; margin: 0; font-size: 28px;">${sendEmailConfig.customHeaderText}</h1>
                            </div>
                            <div style="background: white; padding: 40px 30px; border: 1px solid #e5e7eb; border-top: none;">
                              <div style="white-space: pre-wrap;">${sendEmailConfig.customMessage || 'No message'}</div>
                            </div>
                            <div style="background: #f9fafb; padding: 20px; text-align: center; border-top: 1px solid #e5e7eb; border-radius: 0 0 8px 8px;">
                              <p style="font-size: 12px; color: #9ca3af; margin: 0;">CertifiAble - Because you ARE able! 🤟</p>
                              <p style="font-size: 12px; color: #9ca3af; margin: 5px 0 0 0;">
                                <a href="https://getcertifiable.com" style="color: #3B82F6; text-decoration: none;">getcertifiable.com</a>
                              </p>
                            </div>
                          </div>
                        ` }} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Email Preview: {previewTemplate.templateName}</h3>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setPreviewTemplate(null);
                    setEditingTemplate(previewTemplate);
                    setShowTemplateForm(true);
                  }}
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <button
                  onClick={() => setPreviewTemplate(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-4 pb-4 border-b border-gray-200">
                <p className="text-sm text-gray-500 mb-1">Subject:</p>
                <p className="font-semibold text-gray-900">{previewTemplate.subject}</p>
              </div>
              <div dangerouslySetInnerHTML={{ __html: previewTemplate.bodyHtml }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

